import React, { useLayoutEffect, useState } from 'react';

import { InputLabel } from '@mui/material';
import { AlineInput } from 'src/AlineInput';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  container: {
    alignItems: 'center',
    display: 'flex',
    marginRight: theme.spacing(2),
  },
  input: {
    maxHeight: 24,
    minHeight: 24,
    width: 72,
  },
  label: {
    fontSize: 12,
    padding: theme.spacing(0, 1, 0, 1),
    whiteSpace: 'nowrap',
  },
}));

interface ParagraphPropStepperProps {
  derivedValue: number | null;
  icon: React.ReactNode;
  label: string;
  onChange: (value: number) => Error | void;
  parseValue: (value: string) => number | null;
  stepIncrement: number;
  suffix: string;
  disabled?: boolean;
}

export function ParagraphPropStepper(props: ParagraphPropStepperProps): JSX.Element {
  const {
    derivedValue,
    disabled,
    icon,
    label,
    onChange,
    parseValue,
    stepIncrement,
    suffix,
    ...rest
  } = props;

  const { classes } = useStyles();
  const displayValue = typeof derivedValue === 'number' ? `${derivedValue}${suffix}` : '';
  const [inputValue, setInputValue] = useState(displayValue);

  useLayoutEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  function updateValue(value: number): void {
    const newValue = Math.round(value * 100) / 100;
    const error = onChange(newValue);
    if (error) setInputValue(displayValue);
  }

  return (
    <div className={classes.container} {...rest} role="spinbutton">
      {icon}
      <InputLabel classes={{ root: classes.label }}>{label}</InputLabel>
      <AlineInput
        disabled={disabled}
        fullWidth={false}
        inputClassName={classes.input}
        stepDown={value => {
          if (typeof value !== 'string') return;

          const newValue = parseValue(value);
          typeof newValue !== 'number'
            ? setInputValue(displayValue)
            : updateValue(newValue - stepIncrement);
        }}
        stepUp={value => {
          if (typeof value !== 'string') return;

          const newValue = parseValue(value);
          typeof newValue !== 'number'
            ? setInputValue(displayValue)
            : updateValue(newValue + stepIncrement);
        }}
        type="number"
        value={inputValue}
        onBlur={(): void => {
          const newValue = parseValue(inputValue);
          typeof newValue !== 'number' ? setInputValue(displayValue) : updateValue(newValue);
        }}
        onChange={event => setInputValue(event.target.value)}
        onKeyDown={event => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
      />
    </div>
  );
}
